import React from 'react'
import { useImmerReducer } from 'use-immer'
import { assocPath } from 'ramda'
import deepmerge from 'deepmerge'
import { Layer } from 'models/layer'
import { Project } from 'models/project'
import { LocationSearch } from 'models/geosearch'

export enum Actions {
  selectLayer = 'layer/select',
  deselectLayer = 'layer/deselect',
  changeLayerVisibility = 'layer/visibility-change',
  changeFilters = 'filters/change',
  changeBoundingBox = 'bounding-box/change',
  loadProject = 'project/load',
  setSearch = 'setSearch',
}

type State = {
  activeLayers: number[]
  hiddenLayers: number[]
  filters: any
  editing: Layer | null
  boundingBox: any
  search: LocationSearch | null
}

const initialState: State = {
  activeLayers: [],
  hiddenLayers: [],
  filters: {},
  editing: null,
  boundingBox: {},
  search: null,
}

const ProjectStateContext = React.createContext<State>(initialState)
const ProjectDispatchContext = React.createContext<any | undefined>(undefined)

function reducer(draft: State, action: any) {
  switch (action.type) {
    case Actions.selectLayer:
      draft.activeLayers.push(action.payload)
      return draft

    case Actions.deselectLayer:
      draft.activeLayers = draft.activeLayers.filter((layer: number) => layer !== action.payload)
      return draft

    case Actions.changeLayerVisibility:
      if (action.payload.show) {
        draft.hiddenLayers = draft.hiddenLayers.filter(
          (layer: number) => layer !== action.payload.layerId,
        )
      } else {
        draft.hiddenLayers.push(action.payload.layerId)
      }
      return draft

    case Actions.changeFilters:
      const { sourceid, column } = action.payload
      draft.filters = assocPath([sourceid, column], action.payload, draft.filters)
      return draft

    case Actions.changeBoundingBox:
      draft.boundingBox = action.payload
      return draft

    case Actions.loadProject:
      draft.activeLayers = action.payload.activeLayers
      draft.hiddenLayers = action.payload.hiddenLayers
      draft.filters = action.payload.filters

      return draft

    case Actions.setSearch:
      draft.search = action.payload

      return draft

    default:
      return draft
  }
}

export const ProjectProvider: React.FC<{ defaultValue?: Partial<State> }> = ({
  children,
  defaultValue = {},
}) => {
  const projectInitialState = deepmerge(initialState, defaultValue)
  const [state, dispatch] = useImmerReducer<State>(reducer, projectInitialState)

  return (
    <ProjectStateContext.Provider value={state}>
      <ProjectDispatchContext.Provider value={dispatch}>{children}</ProjectDispatchContext.Provider>
    </ProjectStateContext.Provider>
  )
}

export function useProjectProvider() {
  const context = React.useContext(ProjectStateContext)

  if (typeof context === undefined) {
    throw new Error('useProjectState must be used within a ProjectProvider')
  }

  return context
}

export function useProjectUpdater() {
  const dispatch = React.useContext(ProjectDispatchContext)

  if (typeof dispatch === undefined) {
    throw new Error('useProjectUpdater must be used within a ProjectProvider')
  }

  return {
    selectLayer: (layerId: number) => dispatch({ type: Actions.selectLayer, payload: layerId }),
    deselectLayer: (layerId: number) => dispatch({ type: Actions.deselectLayer, payload: layerId }),
    changeLayerVisibility: (layerId: number, show: boolean) =>
      dispatch({ type: Actions.changeLayerVisibility, payload: { layerId, show } }),
    setFilters: (filters: any) => dispatch({ type: Actions.changeFilters, payload: filters }),
    setBoundingBox: (bbox: any) => dispatch({ type: Actions.changeBoundingBox, payload: bbox }),
    loadProject: (project: Project) => dispatch({ type: Actions.loadProject, payload: project }),
    setSearch: (search: LocationSearch | null) =>
      dispatch({ type: Actions.setSearch, payload: search }),
  }
}

type ProjectContextType = [State, ReturnType<typeof useProjectUpdater>]
export const useProjectContext = (): ProjectContextType => [
  useProjectProvider(),
  useProjectUpdater(),
]
